import PropTypes from "prop-types"
import React, {Suspense, lazy} from 'react';
import {Loader} from 'semantic-ui-react';
import {SemanticToastContainer} from 'react-semantic-toasts';

import { Redirect } from 'react-router-dom';
import { ConnectedRouter } from 'connected-react-router';
import { history } from '../../store';
import { Route } from "react-router-dom";
import AppRoute from './AppRoute';

import {
  canView as canRoleView,
  canCreate as canRoleCreate,
  canEdit as canRoleEdit,
  canDelete as canRoleDelete
} from './permissions';

import actionCable from 'actioncable';

import {Context, store} from './Context';

import 'react-day-picker/lib/style.css';
import 'semantic-ui-css/semantic.min.css';
import { AssistantProvider } from 'features/assistant/context';
import 'react-semantic-toasts/styles/react-semantic-alert.css';
import './app.scss';

import Modal from 'components/pages/Modal';
import RequireLogIn from 'components/pages/RequireLogIn';

import actionCableSubscribe from '../../utils/actioncable';
import {getWSBase} from '../../utils/api/api';
import { TOKEN_AUTH_HEADERS } from 'constants/index';

import {canAccessToPayroll, raisesInvoices} from 'utils/user';

import redux from "features/settings";

import get from 'lodash/get';

import {modalStylesTender, goBackActionTender} from './utils'

import './redesign.scss'
import CompaniesModal from "../../features/requisition-lists/requests/Companies";

const Login = lazy(() => import('components/pages/Login'));
// FIN-1314: the assistant beside the pages, for the people it is switched on for
const Dashboard = lazy(() => import('features/dashboard'));
// FIN-1244: wrapped once, here, not inline in render like the other routes. RequireLogIn
// returns a new component on every call, and App re-renders on every change of
// settings.global — so inline, each pick in the dashboard's vessel picker (which writes
// that slice) remounted the whole page, closing the picker and re-validating the token.
const DashboardPage = RequireLogIn(Dashboard);
const Profile = lazy(() => import('components/pages/Profile'));

const BankForm = lazy(() => import('components/forms/BankForm'));
const CompanyForm = lazy(() => import('features/companies/Form'));
const InvoiceForm = lazy(() => import('components/forms/InvoiceForm'));
const PaymentForm = lazy(() => import('components/forms/PaymentForm'));
const PurchaseOrderNewForm = lazy(() => import('features/purchase-orders/Form'));

const Payroll = lazy(() => import('features/payrolls'));
const PayrollForm = lazy(() => import('features/payrolls/component/Form'));

const MasterAccounts = lazy(() => import('features/master-accounts'));
const MasterAccountMonth = lazy(() => import('features/master-accounts/Month'));
const RecurringTemplates = lazy(() => import('features/recurring-templates'));
const RecurringTemplate = lazy(() => import('features/recurring-templates/Template'));
const InvoiceImports = lazy(() => import('features/invoice-imports'));
const InvoiceImportCard = lazy(() => import('features/invoice-imports/Reconcile'));

const InvoicesNew = lazy(() => import('features/invoices'));
const AssistantPage = lazy(() => import('features/assistant/Page'));
const RequisitionLists = lazy(() => import('features/requisition-lists'));

const DeliveryNote = lazy(() => import('features/delivery-notes'));
const DeliveryNoteForm = lazy(() => import('features/delivery-notes/component/Form'));

const PaymentInstruction = lazy(() => import('features/payment-instructions'));
const PaymentInstructionForm = lazy(() => import('features/payment-instructions/component/Form'));
const PaymentInstructionPaymentForm = lazy(() => import('features/payment-instructions/component/PaymentForm'));

const ServiceFee = lazy(() => import('features/service-fees'));
const ServiceFeeForm = lazy(() => import('features/service-fees/form'));

const PartnerSchemes = lazy(() => import('features/partner-schemes/component/index'));

const Reports = lazy(() => import('components/Reports'));
const GeneralLedger = lazy(() => import('components/Reports/GeneralLedger'));
const BudgetPerformance = lazy(() => import('components/Reports/BudgetPerformance'));
const OALBudgetPerformance = lazy(() => import('components/Reports/OALBudgetPerformance'));
const PNLStatement = lazy(() => import('components/Reports/PNLStatement'));
const MonthlyBudgetPerformance = lazy(() => import('components/Reports/MonthlyBudgetPerformance'));
const ShipRepair = lazy(() => import('components/Reports/ShipRepair'));
const CashFlow = lazy(() => import('components/Reports/CashFlow'));
const CashFlowStatement = lazy(() => import('components/Reports/CashFlowStatement'));
const CashFlowNewStatement = lazy(() => import('components/Reports/CashFlowNewStatement'));
const InvoicesReport = lazy(() => import('components/Reports/Invoices'));
const InvoicesBudgetItemsReport = lazy(() => import('components/Reports/InvoicesBudgetItems'));
const PaymentInstructionsReport = lazy(() => import('components/Reports/PaymentInstructions'));
const Opex = lazy(() => import('components/Reports/OPEX'));
const NotInvoicedPO = lazy(() => import('components/Reports/NotInvoicedPO'));
const PayrollsReport = lazy(() => import('components/Reports/Payrolls'));
const DetailedDelivery = lazy(() => import('components/Reports/DetailedDelivery'));
const OpenOrders = lazy(() => import('components/Reports/OpenOrders'));

const Banks = lazy(() => import('components/pages/Banks'));
const Companies = lazy(() => import('features/companies'));
const Invoices = lazy(() => import('components/pages/Invoices'));
const PurchaseOrders = lazy(() => import('components/pages/PurchaseOrders'));

const CompanyBalance = lazy(() => import('features/companies/balance'));
const TenderTable = lazy(() => import('features/requisition-lists/tenders/TenderTable'));


const connect = redux('global');

class App extends React.Component {
  static propTypes = {
    setCurrentUser: PropTypes.func.isRequired
  }

  state = {
    company: store.company,
    user: store.user,
    invoicesDirection: store.invoicesDirection,
    updateCount: 0,
    cable: actionCable.createConsumer(getWSBase())
  };

  componentDidMount() {
    this.props.setCurrentUser(store.user);
  }

  update = () => {
    this.setState({...this.state, updateCount: this.state.updateCount + 1});
  }

  setItem = (key, value) => {
    store[key] = value;
    this.setState({[key]: value});
  };

  setCompany = (company) => {
    this.setItem('company', company);
  };

  setInvoicesDirection = (invoicesDirection) => {
    this.setItem('invoicesDirection', invoicesDirection);
  };

  setUser = (user) => {
    this.setItem('user', user);
  };

  setInvoicesPage = (invoicesPage) => {
    this.setItem('invoicesPage', invoicesPage);
  };

  setInvoicesYear = (invoicesYear) => {
    this.setItem('invoicesYear', invoicesYear);
  };

  subscribe = (payload, options) => actionCableSubscribe(this.state.cable, payload, options);


  render() {
    const contextValue = {
      ...this.state,
      update: this.update,
      setUser: this.setUser,
      setCompany: this.setCompany,
      setInvoicesDirection: this.setInvoicesDirection,
      setInvoicesPage: this.setInvoicesPage,
      setInvoicesYear: this.setInvoicesYear,
      subscribe: this.subscribe
    };

    const role = this.state.user?.role;

    const canView = (resource) => canRoleView(resource, role);
    const canCreate = (resource) => canRoleCreate(resource, role);
    const canEdit = (resource) => canRoleEdit(resource, role);
    // FIN-1348: New and Copy are Accounting's and Administration's; paying an invoice is not creating one
    const canCreateInvoice = canCreate('invoices') && raisesInvoices(this.state.user);

    return (
      <ConnectedRouter history={history}>
        <Context.Provider value={contextValue}>
          <Suspense fallback={<Loader active inline="centered"/>}>
            <SemanticToastContainer position="top-right"/>
            {/* FIN-1314: the assistant's conversation lives beside the routes; the button is in every page's header */}
            <AssistantProvider>
            <div className="app">
              {/*COMMON ROUTES*/}
              <Route exact path="/" render={() => {
                // Check if user is authenticated
                if (!this.state.user || !TOKEN_AUTH_HEADERS.every(header => localStorage.getItem(header) !== null)) {
                  return <Redirect to="/login" />;
                }
                // Check if user has permission to view invoices
                if (!canView('invoices')) {
                  return <Redirect to="/login" />;
                }
                return <Redirect to="/invoices" />;
              }} />
              <AppRoute component={RequireLogIn(Profile)} path="/profile" allow={canView('profile')} />
              <AppRoute component={DashboardPage} path="/dashboard" allow={canView('dashboard')} />
              <AppRoute component={RequireLogIn(InvoicesNew)} path="/invoices_new" allow={canView('invoices')} />

              <AppRoute component={RequireLogIn(RequisitionLists)} path="/requisition_lists" allow={canView('requisition_lists')} />
              <AppRoute
                component={RequireLogIn(Modal(TenderTable, {size: 'fullscreen'}, modalStylesTender, goBackActionTender))}
                path="/requisition_lists/tenders/:id" allow={canView('requisition_lists')} />
              <AppRoute
                component={RequireLogIn(CompaniesModal)}
                path="/requisition_lists/requests/:id" allow={canView('requisition_lists')}/>


              <AppRoute component={RequireLogIn(Invoices)} path="/invoices" allow={canView('invoices')} />
              <AppRoute component={RequireLogIn(Modal(InvoiceForm))} path="/(invoices|invoices_new)/create" allow={canCreateInvoice} />
              <AppRoute component={RequireLogIn(Modal(InvoiceForm))} path="/(invoices|invoices_new)/copy/:fromId(\d+)" allow={canCreateInvoice} />
              <AppRoute component={RequireLogIn(Modal(PaymentForm))} path="/(invoices|invoices_new)/pay/:invoiceId(\d+)" allow={canCreate('invoices')} />
              <AppRoute component={RequireLogIn(Modal(InvoiceForm))} path="/(invoices|invoices_new)/:id(\d+)" allow={canEdit('invoices') || canView('invoices')} />

              <AppRoute component={RequireLogIn(Banks)} path="/banks" allow={canView('banks')} />
              <AppRoute component={RequireLogIn(Modal(BankForm, {size: 'small'}))} path="/banks/create" allow={canCreate('banks')} />
              <AppRoute component={RequireLogIn(Modal(BankForm, {size: 'small'}))} path="/banks/:id(\d+)" allow={canEdit('banks')} />

              <AppRoute component={RequireLogIn(Companies)} path="/companies" allow={canView('companies')} />
              <AppRoute component={RequireLogIn(Modal(CompanyForm))} path="/companies/create" allow={canCreate('companies')} />
              <AppRoute component={RequireLogIn(Modal(CompanyForm))} path="/companies/:id(\d+)" allow={canEdit('companies')} />

              <AppRoute component={RequireLogIn(PurchaseOrders)} path="/purchase_orders" allow={canView('purchase_orders')} />
              <AppRoute component={RequireLogIn(Modal(PurchaseOrderNewForm))} path="/purchase_orders/create" allow={canCreate('purchase_orders')} />
              <AppRoute component={RequireLogIn(Modal(PurchaseOrderNewForm))} path="/purchase_orders/copy/:fromId(\d+)" allow={canCreate('purchase_orders')} />
              <AppRoute component={RequireLogIn(Modal(PurchaseOrderNewForm))} path="/purchase_orders/:id(\d+)/edit" allow={canEdit('purchase_orders') || canView('purchase_orders')} />

              <AppRoute component={RequireLogIn(PaymentInstruction)} path="/payment_instructions" allow={canView('payment_instructions')} />
              <AppRoute component={RequireLogIn(Modal(PaymentInstructionForm))} path="/payment_instructions/create" allow={canCreate('payment_instructions')} />
              <AppRoute component={RequireLogIn(Modal(PaymentInstructionForm))}
                     path="/payment_instructions/:id(\d+)/edit" allow={canEdit('payment_instructions') || canView('payment_instructions')} />
              <AppRoute component={RequireLogIn(Modal(PaymentInstructionPaymentForm))}
                     path="/payment_instructions/:id(\d+)/pay" allow={canEdit('payment_instructions')} />

              <AppRoute component={RequireLogIn(DeliveryNote)} path="/delivery_notes" allow={canView('delivery_notes')} />
              <AppRoute component={RequireLogIn(Modal(DeliveryNoteForm))} path="/delivery_notes/create" allow={canCreate('delivery_notes')} />
              <AppRoute component={RequireLogIn(Modal(DeliveryNoteForm))} path="/delivery_notes/:id(\d+)/edit" allow={canEdit('delivery_notes') || canView('delivery_notes')} />

              <AppRoute component={RequireLogIn(ServiceFee)} path="/service_fees" allow={canView('service_fees')} />
              <AppRoute component={RequireLogIn(Modal(ServiceFeeForm))} path="/service_fees/create" allow={canCreate('service_fees')} />
              <AppRoute component={RequireLogIn(Modal(ServiceFeeForm))} path="/service_fees/:id(\d+)/edit" allow={canEdit('service_fees') || canView('service_fees')} />

              {/*ADMIN ONLY ROUTES*/}
              {get(this.state.user, 'role') === 'admin' &&
                <>
                  <AppRoute component={RequireLogIn(CompanyBalance)} path="/companies_balance" allow={canView('companies_balance')} />
                  <AppRoute component={RequireLogIn(PartnerSchemes)} path="/partner_schemes" allow={canView('partner_schemes')} />
                </>
              }

              {/*PAYROLL ACCESS ONLY ROUTES*/}
              {canAccessToPayroll(this.state.user) &&
                <>
                  <AppRoute component={RequireLogIn(Payroll)} path="/payrolls" allow={canView('payrolls')} />
                  <AppRoute component={RequireLogIn(Modal(PayrollForm, {size: 'fullscreen'}))} path="/payrolls/create" allow={canCreate('payrolls')} />
                  <AppRoute component={RequireLogIn(Modal(PayrollForm, {size: 'fullscreen', cancelName: 'Exit'}))}
                         path="/payrolls/:id(\d+)/edit" allow={canEdit('payrolls')} />
                  <AppRoute component={RequireLogIn(Modal(PayrollsReport, {size: 'fullscreen'}))}
                         path="/reports/payrolls" allow={canView('report_payrolls')} />
                  {/* The Master Account months DAX publishes (FIN-1263). The
                      month opens as a dialog over the list, like every other
                      document here — so the list route is NOT exact: it stays
                      mounted underneath and the row is still there on close. */}
                  <AppRoute component={RequireLogIn(MasterAccounts)}
                         path="/master_accounts" allow={canView('master_accounts')} />
                  <AppRoute component={RequireLogIn(Modal(MasterAccountMonth, {size: 'fullscreen', cancelName: 'Close'}))}
                         path="/master_accounts/:id(\d+)" allow={canView('master_accounts')} />
                </>
              }
              {/* FIN-1266: the recurring templates — the list, and the template as a
                  dialog over it, so the list stays mounted underneath. */}
              <AppRoute component={RequireLogIn(RecurringTemplates)}
                     path="/recurring_templates" allow={canView('recurring_templates')} />
              <AppRoute component={RequireLogIn(Modal(RecurringTemplate, {size: 'fullscreen', cancelName: 'Close'}))}
                     path="/recurring_templates/:id(\d+)" allow={canView('recurring_templates')} />
              {/* FIN-1281: supplier invoices read from the mailbox — the queue, and the card of
                  one import as a dialog over it. */}
              <AppRoute component={RequireLogIn(InvoiceImports)}
                     path="/invoice_imports" allow={canView('invoice_imports')} />
              <AppRoute component={RequireLogIn(Modal(InvoiceImportCard, {size: 'fullscreen', cancelName: 'Close'}))}
                     path="/invoice_imports/:id(\d+)" allow={canView('invoice_imports')} />
              {/*REPORTS*/}
              <AppRoute component={RequireLogIn(Reports)} path="/reports" allow={canView('reports')} />
              <AppRoute component={RequireLogIn(Modal(GeneralLedger, {size: 'fullscreen'}))}
                     path="/reports/general_ledger" allow={canView('report_general_ledger')} />
              <AppRoute component={RequireLogIn(Modal(BudgetPerformance, {size: 'fullscreen'}))}
                     path="/reports/budget_performance" allow={canView('report_budget_performance')} />
              <AppRoute component={RequireLogIn(Modal(OALBudgetPerformance, {size: 'fullscreen'}))}
                     path="/reports/oal_budget_performance" allow={canView('report_oal_budget_performance')} />
              <AppRoute component={RequireLogIn(Modal(PNLStatement, {size: 'fullscreen'}))} path="/reports/pnl_statement" allow={canView('report_pnl_statement')} />
              <AppRoute component={RequireLogIn(Modal(CashFlowNewStatement, {size: 'fullscreen'}))}
                     path="/reports/cash_flow_new_statement" allow={canView('report_cash_flow_new_statement')} />
              <AppRoute component={RequireLogIn(Modal(MonthlyBudgetPerformance, {size: 'fullscreen'}))}
                     path="/reports/monthly_budget_performance" allow={canView('report_monthly_budget_performance')} />
              <AppRoute component={RequireLogIn(Modal(ShipRepair, {size: 'fullscreen'}))} path="/reports/ship_repair" allow={canView('report_ship_repair')} />
              <AppRoute component={RequireLogIn(Modal(CashFlow, {size: 'fullscreen'}))} path="/reports/cash_flow" allow={canView('report_cash_flow')} />
              <AppRoute component={RequireLogIn(Modal(CashFlowStatement, {size: 'fullscreen'}))}
                     path="/reports/cash_flow_statement" allow={canView('report_cash_flow_statement')} />
              <AppRoute component={RequireLogIn(Modal(InvoicesReport, {size: 'fullscreen'}))} path="/reports/invoices" allow={canView('report_invoices')} />
              <AppRoute component={RequireLogIn(Modal(InvoicesBudgetItemsReport, {size: 'fullscreen'}))}
                     path="/reports/invoices_budget_items" allow={canView('report_invoices_budget_items')} />
              <AppRoute component={RequireLogIn(Modal(PaymentInstructionsReport, {size: 'fullscreen'}))}
                     path="/reports/payment_instructions" allow={canView('report_payment_instructions')} />
              <AppRoute component={RequireLogIn(Modal(Opex, {size: 'fullscreen'}))} path="/reports/opex" allow={canView('report_opex')} />
              <AppRoute component={RequireLogIn(Modal(NotInvoicedPO, {size: 'fullscreen'}))}
                     path="/reports/not_invoiced_po" allow={canView('report_not_invoiced_po')} />
              <AppRoute component={RequireLogIn(Modal(DetailedDelivery, {size: 'fullscreen'}))}
                     path="/reports/detailed_delivery" allow={canView('report_detailed_delivery')} />
              <AppRoute component={RequireLogIn(Modal(OpenOrders, {size: 'fullscreen'}))}
                     path="/reports/open_orders" allow={canView('report_open_orders')} />
              {/* FIN-1314: the chats page, for the people the assistant is switched on for */}
              <AppRoute component={RequireLogIn(AssistantPage)} path="/assistant/:id?" allow={Boolean(this.state.user?.ai_assistant)} />
              <Route component={Login} path="/login" />
            </div>
            </AssistantProvider>
          </Suspense>
        </Context.Provider>
      </ConnectedRouter>
    );
  }
}


export default connect(App);
